/**
 * A campaign group's auto-send as three pills (user, 20/10/2026: "Simulated
 * — on the real containers, it shows how it will look; Live — it really
 * sends"): Off · Simulated (stored as shadow: decides exactly as Live, sends
 * nothing) · Live. Simulated shows to who holds "Automation Simulated
 * auto-send" (admins); Live to who holds "Automation Live auto-send". Live
 * always means real SMS (user, 20/10/2026: "the point of live is to make it
 * real"): on a server that never sends real SMS it shows greyed, saying why.
 * The group's current state always shows.
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  AUTOSEND_REAL_PERMISSION,
  AUTOSEND_SIMULATE_PERMISSION,
  type DropGenAutoSendMode,
  type DropGenServerSendModes,
} from '@trafficpulse/shared';

import { useAuth } from '@/providers/AuthProvider';

import { useSchedulerStatus } from '../../../../useDropGenerator';

export interface ModeOption {
  value: DropGenAutoSendMode;
  label: string;
  tone: 'contrast' | 'warning' | 'success';
  disabled?: boolean;
  title?: string;
}

const OFF: ModeOption = { value: 'off', label: 'Off', tone: 'contrast' };
const SIMULATED: ModeOption = { value: 'shadow', label: 'Simulated', tone: 'warning' };
const LIVE: ModeOption = { value: 'on', label: 'Live', tone: 'success' };

/** A container's own layer: "On" = on while its group is; Simulated decides and sends nothing. */
export const CONTAINER_MODE_OPTIONS: ModeOption[] = [OFF, SIMULATED, { value: 'on', label: 'On', tone: 'success' }];

export interface ModeRights {
  canLive: boolean;
  canSimulate: boolean;
  server: DropGenServerSendModes | undefined;
}

/** The server sends real SMS. Not read yet = yes (Bugbot on #278): Live never greys out
 *  during the first paint — the API says why not. */
export const serverSendsReal = (server: DropGenServerSendModes | undefined) => !server || server.real;

export const NO_REAL_SENDS =
  'Live sends real SMS — this server never does. It works on production, or here once real sending is switched on (DROP_GENERATOR_AUTOSEND_REAL=true, a developer setting).';

export function modeOptions(r: ModeRights, current?: DropGenAutoSendMode | null): ModeOption[] {
  const out = [OFF];
  if (r.canSimulate || current === 'shadow') out.push(SIMULATED);
  if (current === 'on') out.push(LIVE);
  else if (r.canLive) out.push(serverSendsReal(r.server) ? LIVE : { ...LIVE, disabled: true, title: NO_REAL_SENDS });
  return out;
}

/** What one "on" switch means for this person: Live where they may and the server sends real SMS,
 *  else Simulated when they may, else nothing — the switch cannot turn on (Bugbot on #281). */
export const onModeFor = (r: ModeRights): 'on' | 'shadow' | null =>
  r.canLive && serverSendsReal(r.server) ? 'on' : r.canSimulate ? 'shadow' : null;

export const modePatch = (mode: DropGenAutoSendMode) => ({ enabled: mode !== 'off', shadow: mode === 'shadow' });

/** What a Live send does on this server — the confirm's line. */
export const liveKind = (server: DropGenServerSendModes | undefined): 'real' | 'simulated' => (server && !server.real && server.simulated ? 'simulated' : 'real');

export function useModeRights(): ModeRights {
  const { hasPermission } = useAuth();
  return {
    canLive: hasPermission(AUTOSEND_REAL_PERMISSION),
    canSimulate: hasPermission(AUTOSEND_SIMULATE_PERMISSION),
    server: useSchedulerStatus().data?.sendModes,
  };
}
